import { apiClient } from '../../../core/api/client';
import { OwnerProviderDocument } from '../types/provider-profile.types';

/**
 * Feature 073 — PDF documents attached to a Service Provider profile.
 *
 * Two write surfaces hit the same row shape:
 *   • Admins   → /admin/providers/:providerId/documents
 *   • SP-self  → /service-providers/me/documents (providerId derived from JWT)
 *
 * Both return the OwnerProviderDocument shape (counters included).
 * The public profile DTO exposes a different, counter-less shape via
 * `ProviderProfile.documents`.
 */

export type CreateProviderDocumentInput = {
  fileKey: string;
  fileUrl: string;
  fileSize: number;
  displayName: string;
  description?: string | null;
};

export type UpdateProviderDocumentInput = {
  displayName?: string;
  description?: string | null;
};

export type UploadedFileResponse = {
  fileKey: string;
  publicUrl: string;
};

/**
 * Raw upload helper — posts to /files/upload and returns BOTH `fileKey` and
 * `publicUrl`. The shared `useFileUpload` hook drops `fileKey`, but documents
 * need it for tracking/cleanup so we issue our own request here.
 */
export const uploadProviderDocumentBinary = async (
  file: File,
): Promise<UploadedFileResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<UploadedFileResponse>(
    '/files/upload',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
};

// =====================================================================
// Admin endpoints — operate on any SP's documents
// =====================================================================

export const listAdminProviderDocuments = async (
  providerId: string,
): Promise<OwnerProviderDocument[]> => {
  const { data } = await apiClient.get<OwnerProviderDocument[]>(
    `/admin/providers/${providerId}/documents`,
  );
  return data;
};

export const createAdminProviderDocument = async (
  providerId: string,
  input: CreateProviderDocumentInput,
): Promise<OwnerProviderDocument> => {
  const { data } = await apiClient.post<OwnerProviderDocument>(
    `/admin/providers/${providerId}/documents`,
    input,
  );
  return data;
};

export const updateAdminProviderDocument = async (
  providerId: string,
  docId: string,
  input: UpdateProviderDocumentInput,
): Promise<OwnerProviderDocument> => {
  const { data } = await apiClient.patch<OwnerProviderDocument>(
    `/admin/providers/${providerId}/documents/${docId}`,
    input,
  );
  return data;
};

export const deleteAdminProviderDocument = async (
  providerId: string,
  docId: string,
): Promise<void> => {
  await apiClient.delete(`/admin/providers/${providerId}/documents/${docId}`);
};

// =====================================================================
// SP-self endpoints — operate on the caller's own profile
// =====================================================================

export const listMyProviderDocuments = async (): Promise<
  OwnerProviderDocument[]
> => {
  const { data } = await apiClient.get<OwnerProviderDocument[]>(
    '/service-providers/me/documents',
  );
  return data;
};

export const createMyProviderDocument = async (
  input: CreateProviderDocumentInput,
): Promise<OwnerProviderDocument> => {
  const { data } = await apiClient.post<OwnerProviderDocument>(
    '/service-providers/me/documents',
    input,
  );
  return data;
};

export const updateMyProviderDocument = async (
  docId: string,
  input: UpdateProviderDocumentInput,
): Promise<OwnerProviderDocument> => {
  const { data } = await apiClient.patch<OwnerProviderDocument>(
    `/service-providers/me/documents/${docId}`,
    input,
  );
  return data;
};

export const deleteMyProviderDocument = async (docId: string): Promise<void> => {
  await apiClient.delete(`/service-providers/me/documents/${docId}`);
};
